'use strict';

/**
 * A persistent id for THIS browser/device, used only to tell "same account,
 * same device reconnecting" apart from "same account, a genuinely different
 * device" - see SocketGateway's "join" handler (server/src/realtime/
 * SocketGateway.ts) and docs/architecture.md. Not an identity/auth
 * mechanism - the session cookie already handles who the user is; this is
 * purely "which of that user's devices is this".
 *
 * Persisted in localStorage (not sessionStorage) so it survives a reload
 * and stays stable across tabs of the same browser - which is exactly what
 * lets two tabs of the same browser count as "the same device" (no
 * takeover prompt), while a different browser or a different computer gets
 * a fresh id (a real conflict).
 */
const KEY = 'watchparty_device_id';

let cached: string | null = null;

export function getDeviceId(): string {
  if (cached) return cached;
  try {
    let id = localStorage.getItem(KEY);
    if (!id) {
      id = crypto.randomUUID();
      localStorage.setItem(KEY, id);
    }
    cached = id;
    return id;
  } catch {
    // Private browsing / storage disabled: fall back to a per-page-load id.
    // Every socket "join" from this tab still sends the same value (kept in
    // `cached`), so it behaves fine as long as the page stays open - it
    // just won't survive a reload, which only means a reload always looks
    // like "a different device" to the server's conflict check above.
    cached = crypto.randomUUID();
    return cached;
  }
}
